import type { IBrazilianIssuerDividendsQuery } from "lib/queries/__generated__/BrazilianIssuerDividendsQuery.generated";
import type { IDividendsPaidDetails, IDividendYieldDetails } from "types/graphqlTypes";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/Select";
import classNames from "components/ui/classNames";
import { FormattedMessage, useIntl } from "react-intl";
import { useMemo, useState } from "react";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import getYear from "date-fns/getYear";
import parseDateYmd from "lib/utils/parseDateYmd";
import PlotTypeSelector from "components/charts/modern/inputs/PlotTypeSelector";
import { BarChartIcon, TableIcon } from "lucide-react";
import BrazilianDividendsPaid from "components/company/v3/brazilian/contents/events/dividends/BrazilianDividendsPaid";
import BrazilianDividendsPaidByYear from "components/company/v3/brazilian/contents/events/dividends/BrazilianDividendsPaidByYear";
import type { TDividendsPaidByYear } from "components/company/v3/brazilian/contents/events/dividends/types";
import BrazilianDividendsPaidChart from "components/company/v3/brazilian/contents/events/dividends/BrazilianDividendsPaidChart";
import BrazilianDividendsPaidByYearChart from "components/company/v3/brazilian/contents/events/dividends/BrazilianDividendsPaidByYearChart";
import parseISO from "date-fns/parseISO";
import { SimpleTextTooltip } from "components/ui/Tooltip";
import getDividendYield from "components/company/v3/utils/getDividendYield";

interface BrazilianDividendsContentProps {
  tickerSymbol: string;
  loading: boolean;
  filterDividendsByYear: number | null;
  setFilterDividendsByYear: (year: number | null) => void;
  data: IBrazilianIssuerDividendsQuery["dividendsPaid"]["nodes"];
  availableYears: IBrazilianIssuerDividendsQuery["dividendsPaid"]["availableYears"];
  dividendYieldSnapshotEachSymbol: IDividendYieldDetails[];
  isRenderPageDefault: boolean;
  setEnabledFullHeight: (enabled: boolean) => void;
}

export default function BrazilianDividendsContent({
  tickerSymbol,
  loading,
  filterDividendsByYear,
  setFilterDividendsByYear,
  data,
  availableYears,
  dividendYieldSnapshotEachSymbol,
  isRenderPageDefault,
  setEnabledFullHeight,
}: BrazilianDividendsContentProps) {
  const [groupedByYear, setGroupedByYear] = useState(false);
  const [plotType, setPlotType] = useState<"column" | "table">("table");

  const intl = useIntl();

  const dividendsPaid = data
    .filter((node) => filterActionsByTickerSymbol(tickerSymbol, node))
    .filter((node) => getYear(parseISO(node.recordDate)) >= 1995);

  const dividendsPaidByYear = useMemo(() => {
    return dividendsPaid.reduce((dividendsByYear, node) => {
      const year = getYear(parseDateYmd(node.recordDate));
      const candidate = dividendsByYear.get(year);

      if (!candidate) {
        dividendsByYear.set(year, {
          year,
          dividendsPaid: [node],
          amountPaid: node.amountPaid,
        });
      } else {
        dividendsByYear.set(year, {
          year,
          dividendsPaid: [...candidate.dividendsPaid, node],
          amountPaid: candidate.amountPaid + node.amountPaid,
        });
      }

      return dividendsByYear;
    }, new Map<number, TDividendsPaidByYear>());
  }, [dividendsPaid]);

  if (loading && !data) {
    return (
      <div className="flex h-full w-full flex-col">
        <LoadingSkeleton count={1} className="h-full min-h-96" />
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col items-end space-y-4">
      <div className="flex w-full flex-col items-center justify-between gap-4 sm:flex-row">
        <div className="grid w-full grid-cols-2 items-center justify-start gap-4 lg:flex">
          <div className="w-full md:w-36">
            <Select
              value={filterDividendsByYear ? String(filterDividendsByYear) : "ALL"}
              disabled={groupedByYear}
              onValueChange={(value) => {
                if (value === "ALL") {
                  return setFilterDividendsByYear(null);
                }

                return setFilterDividendsByYear(parseInt(value, 10));
              }}
            >
              <SelectTrigger
                className={classNames(
                  "font-aeonik",
                  "w-full rounded-lg",
                  "border border-slate-200 bg-white",
                  "text-sm font-medium tracking-wide text-slate-600",
                  "disabled:select-none",
                  "hover:border-transparent hover:ring-4 hover:ring-slate-100 hover:ring-offset-1 hover:ring-offset-slate-300",
                  "focus-visible:border-transparent focus-visible:ring-4 focus-visible:ring-indigo-100 focus-visible:ring-offset-1 focus-visible:ring-offset-indigo-300",
                  "truncate",
                )}
              >
                <SelectValue
                  placeholder={intl.formatMessage({
                    defaultMessage: "Filtre o ano",
                    id: "0HA0Sr",
                  })}
                />
              </SelectTrigger>
              <SelectContent className="rounded-lg border border-slate-200 text-sm" side="top">
                <SelectItem value="ALL">
                  {filterDividendsByYear === null
                    ? intl.formatMessage({
                        defaultMessage: "Filtre o ano",
                        id: "0HA0Sr",
                      })
                    : intl.formatMessage({
                        defaultMessage: "Remover filtro",
                        id: "J5Id85",
                      })}
                </SelectItem>

                {availableYears.map((year) => (
                  <SelectItem key={`selectBoxYear/${year}`} value={String(year)} className="w-full">
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <button
            type="button"
            className={classNames(
              "h-10 rounded-lg px-4 py-2 text-sm font-medium text-slate-600",
              "leading-5 tracking-normal",
              "border border-slate-200 bg-slate-50",
              "hover:bg-indigo-50 hover:text-indigo-600",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-300 focus-visible:ring-offset-2",
              "focus-visible:border-transparent",
              "disabled:cursor-not-allowed",
              {
                "bg-indigo-50 text-indigo-600": groupedByYear,
              },
            )}
            onClick={() => {
              if (groupedByYear === false) {
                setGroupedByYear(true);
                setFilterDividendsByYear(null);
              } else {
                setGroupedByYear(false);
              }
            }}
          >
            <FormattedMessage defaultMessage="Agrupar por ano" id="wE4cTx" />
          </button>

          <div
            className={classNames({
              "col-span-2": groupedByYear,
            })}
          >
            <PlotTypeSelector
              types={[
                {
                  type: "table",
                  label: intl.formatMessage({
                    id: "jZHHzp",
                    defaultMessage: "Tabela",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <TableIcon className="h-5 w-5" />,
                },
                {
                  type: "column",
                  label: intl.formatMessage({
                    id: "hZ073v",
                    defaultMessage: "Barra",
                    description: "Tipo de visualização do gráfico",
                  }),
                  icon: <BarChartIcon className="h-5 w-5" />,
                },
              ]}
              value={plotType}
              onChange={(value) => {
                setPlotType(value);
                if (value === "column") {
                  setEnabledFullHeight(true);
                } else {
                  setEnabledFullHeight(false);
                }
              }}
            />
          </div>

          {!groupedByYear && dividendYieldSnapshotEachSymbol.length > 0 && (
            <SimpleTextTooltip
              content={<FormattedMessage defaultMessage="Dividend Yield dos últimos 12 meses" id="z30HiR" />}
            >
              <div
                className={classNames(
                  "flex justify-center whitespace-nowrap rounded-lg px-4 py-2 text-sm text-slate-600 lg:hidden",
                  "bg-yellow-600/10 font-bold text-yellow-600",
                )}
              >
                DY {getDividendYield(dividendYieldSnapshotEachSymbol, tickerSymbol)}%
              </div>
            </SimpleTextTooltip>
          )}
        </div>

        {groupedByYear && (
          <p className="whitespace-nowrap text-xs italic tracking-wider">
            *<FormattedMessage defaultMessage="Valores baseados na Data-com dos proventos." id="CuLis2" />
          </p>
        )}

        {!groupedByYear && dividendYieldSnapshotEachSymbol.length > 0 && (
          <SimpleTextTooltip
            content={<FormattedMessage defaultMessage="Dividend Yield dos últimos 12 meses" id="z30HiR" />}
          >
            <div
              className={classNames(
                "hidden whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium text-slate-600 lg:flex",
                "bg-yellow-600/10 font-bold text-yellow-600",
              )}
            >
              DY {getDividendYield(dividendYieldSnapshotEachSymbol, tickerSymbol)}%
            </div>
          </SimpleTextTooltip>
        )}
      </div>

      <div className="flex h-full w-full flex-col items-end space-y-4">
        {plotType === "table" && !groupedByYear && (
          <BrazilianDividendsPaid dividendsPaid={dividendsPaid} isRenderPageDefault={isRenderPageDefault} />
        )}

        {plotType === "table" && groupedByYear && (
          <BrazilianDividendsPaidByYear
            dividendsPaid={Array.from(dividendsPaidByYear.values())}
            isRenderPageDefault={isRenderPageDefault}
          />
        )}

        {plotType === "column" && groupedByYear && (
          <BrazilianDividendsPaidByYearChart dividendsPaid={Array.from(dividendsPaidByYear.values())} />
        )}

        {plotType === "column" && !groupedByYear && <BrazilianDividendsPaidChart dividendsPaid={dividendsPaid} />}
      </div>
    </div>
  );
}

function filterActionsByTickerSymbol(tickerSymbol: string | null, node: IDividendsPaidDetails) {
  if (tickerSymbol && tickerSymbol === node.tickerSymbol) {
    return true;
  }

  if (!tickerSymbol) {
    return true;
  }

  return false;
}
